import { css, SerializedStyles } from '@emotion/react';
import { CSSProperties } from 'react';
import { colors, breakpoints, spacing, space } from '@airwallex/global';
import { LinkInfoData, OptionStateStyle } from './types';
import { Gradients } from '@/components/layout/colors';
import BREAK_POINTS from '@/components/common/breakpoints';

const { ultraViolet, white, greys, softGrey, red } = colors;

export const getIconWidth = (status: LinkInfoData['status']): string => {
  switch (status) {
    case 'PAID':
      return '140px';
    case 'EXPIRED':
      return '270px';
    case 'DISABLED':
      return '270px';
    default:
      return '190px';
  }
};

const mobilePaddingCenter = css`
  ${breakpoints.breakpoint.s} {
    padding: 0 24px;
  }
`;
export const mobileTextAlignCenter = css`
  ${breakpoints.breakpoint.s} {
    text-align: center;
  }
`;
export const formCss = css`
  @media (min-width: 1121px) {
    width: 1120px;
  }
  @media (max-width: 1120px) {
    width: 100vw;
  }
  @media (min-width: ${breakpoints.breakpointTokens.s + 1}px) {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin: 96px auto 0 auto;
  }
  ${breakpoints.breakpoint.s} {
    margin-top: 64px;
    padding-bottom: 56px;
    width: 480px;
    max-width: 100vw;
    margin: 0 auto;
  }
  ${mobilePaddingCenter}
`;

export const amountContainerCss = css`
  display: flex;
  flex-direction: row;
  margin-top: -4px;
  margin-bottom: 4px;
  justify-content: space-between;
  @media (max-width: ${BREAK_POINTS.mobile}px) {
    justify-content: center;
  }
`;

export const submitCss = (disabled: boolean): SerializedStyles => css`
  margin-top: 40px;
  height: 40px;
  width: 100%;
  color: #fff;
  text-align: center;
  box-shadow: none;
  border: none;
  cursor: pointer;
  border-radius: 6px;
  ${breakpoints.breakpoint.s} {
    position: sticky;
    // when height is not enough, it will be fixed at the bottom
    // otherwise, it will be at the end of the flow
    top: 10000px;
    width: 100%;
    margin-top: 24px;
  }
  ${disabled
    ? css`
        cursor: not-allowed;
        &:hover {
          color: ${white};
        }
      `
    : css`
        &:hover {
          background-color: rgb(112, 67, 255);
          color: ${white};
          box-shadow: none;
        }
      `}
`;

export const headlineCss = css`
  ${mobileTextAlignCenter}
  word-wrap: break-word;
  margin-top: ${space.s};
  ${breakpoints.breakpoint.s} {
    margin-top: ${space.m};
  }
`;

export const fixAmountCss = css`
  margin-top: ${spacing.l}px;
  margin-bottom: ${spacing.l}px;
  font-size: 24px;
  ${mobileTextAlignCenter}
`;

export const referenceCss = css`
  ${breakpoints.breakpoint.s} {
    text-align: center;
  }
`;

export const paragraphCss = css`
  margin-top: '8px';
  word-wrap: break-word;
  ${mobileTextAlignCenter}
  ${breakpoints.breakpoint.s} {
    margin-top: 24px;
    font-size: 14px;
    font-weight: 400;
  }
`;

export const amountCss = (fixedCurrency: string | undefined): SerializedStyles => css`
  width: ${fixedCurrency ? '150px' : '430px'};
  padding-left: 0px;
  padding-right: 0px;
  input {
    height: 56px;
    font-size: 24px;
    font-weight: 700;
  }
  &:not(:only-child) {
    input {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
`;

export const descriptionCss = css`
  margin-top: ${space.s};
  margin-bottom: ${space.xl};
  word-wrap: break-word;
  white-space: pre-line;
  ${mobileTextAlignCenter}
  ${breakpoints.breakpoint.s} {
    margin-top: ${space.s};
    margin-bottom: 0;
  }
`;

export const Css = css`
  ${breakpoints.breakpoint.s} {
    text-align: center;
  }
`;

export const expiryCss = css`
  margin-top: 8px;
  color: ${greys[60]};
`;

export const expiryDateCss = css`
  font-weight: bold;
`;

export const selectCss = css`
  width: 120px;
  padding-left: 0px;
  padding-right: 0px;
  margin-left: 8px;
`;

export const nameCss = css`
  margin-top: ${space.l};
  margin-bottom: ${space.s};
  padding-top: 0px;
  padding-bottom: 0px;
  ${breakpoints.breakpoint.s} {
    margin-top: ${space.xl};
  }
`;

export const nonFirstInputCss = css`
  margin-top: ${space.s};
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
`;

export const emailCss = css`
  ${breakpoints.breakpoint.s} {
    width: 100%;
  }
  margin-top: 40px;
  margin-bottom: 4px;
  width: calc(100% - 128px);
`;

export const checkboxCss = css`
  margin-bottom: 24px;
`;

export const bannerContainerCss = css`
  position: absolute;
  height: 196px;
  width: 100%;
  z-index: -1;
  left: 0;
  background: ${Gradients.headerBg};
`;

export const imgContainerCss = (linkInfo?: LinkInfoData): SerializedStyles => css`
  margin: ${linkInfo?.status === 'EXPIRED' ? '40px auto 0' : '74px auto 0'};
  max-width: 480px;
  text-align: center;
  ${mobilePaddingCenter}
`;

export const btnCss = css`
  height: 40px;
  border-radius: 6px;
  border: 1px solid ${greys[20]};
  font-weight: bold;
  color: ${ultraViolet};
  box-shadow: none;
  padding: 0 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;
export const reloadAndBackBtnCss = css`
  ${btnCss}
  margin-top: 24px;
  background-color: ${white} !important;
`;

export const primaryBtnCss = css`
  ${btnCss}
  color: ${white};
  margin-top: 24px;
  background-color: ${ultraViolet} !important;
`;

export const imgCss = (linkInfo?: LinkInfoData): SerializedStyles => css`
  width: ${getIconWidth(linkInfo?.status)};
`;

export const errorCss = css`
  margin-top: 8px;
  color: ${red};
`;

export const headCss = css`
  margin: 30px auto;
  max-width: 800px;
  display: flex;
  align-items: center;
  flex-direction: column;
  ${mobilePaddingCenter}
`;

export const headerCss = css`
  margin-top: 48px;
  text-align: center;
`;

export const bodyCss = css`
  text-align: center;
  margin-top: 16px;
`;

export const bannerLogoContainerCss = css`
  display: flex;
  justify-content: center;
`;

export const cardContainer = css`
  @media (max-width: 540px) {
    width: calc(100% - 16px);
    padding: 32px 16px;
  }
  width: 540px;
  margin: 0;
  padding: 60px 40px 32px;
  border-radius: 6px;
  box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.08), 0 0 16px 0 rgba(0, 0, 0, 0.08);
  ${breakpoints.breakpoint.s} {
    margin-top: 32px auto 0;
  }
`;

export const fieldCss = css`
  color: ${greys[90]};
  word-break: break-word;
  max-width: 160px;
  width: 100%;
`;

export const valueCss = css`
  ${fieldCss};
  text-align: right;
  max-width: 308px;
  width: 100%;
`;

export const displayInfoCss = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 4px;
  &:last-of-type {
    padding-bottom: 0px;
  }
`;

export const titleCss = css`
  margin-bottom: 16px;
`;

export const dividerCss = css`
  width: 100%;
`;

export const linkCss = css`
  color: ${ultraViolet};
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
`;

export const contactInfoCss = css`
  margin-top: 40px;
  font-size: 12px;
`;

export const emailContainerCss = css`
  width: calc(100% + 8px);
  margin-left: -8px;
  ${breakpoints.breakpoint.s} {
    max-width: 540px;
    margin: 0 auto;
    width: calc(100% - 16px);
  }
  ${breakpoints.breakpoint.s} {
    display: flex;
    justify-content: start;
    align-items: start;
  }
`;

export const emailBtnCss = (disabled: boolean): SerializedStyles => css`
  ${breakpoints.breakpoint.s} {
    width: 100%;
    margin-top: 8px;
    margin-left: 0px;
  }
  height: 40px;
  border-radius: 6px;
  border: 1px solid ${greys[20]};
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${disabled ? 'default' : 'pointer'};
  color: ${disabled ? 'rgba(0, 0, 0, 0.26)' : ultraViolet};
  margin-top: 72px;
  margin-left: 8px;
  width: 136px;
  background-color: ${disabled ? greys[20] : white};
  &:hover {
    background-color: ${white};
    color: ${ultraViolet};
    box-shadow: none;
  }

  svg {
    margin-right: 8px;
  }
`;

export const receiptSubTitle = css`
  margin-top: 16px;
  margin-bottom: 16px;
`;

export const alterWarningCss = css`
  ${breakpoints.breakpoint.s} {
    margin-top: 40px;
  }
`;

export const logoCenterContainerBasicCss = css`
  width: 168px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const logoContainerBasicCss = css`
  width: 185px;
  height: 62px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 56px;
`;

export const logoImgBasicCss = css`
  max-width: 100%;
  max-height: 100%;
`;

export const receiptLogoImgCss = css`
  ${logoImgBasicCss}
  margin-bottom: 40px;
`;

export const logoContainerFormCss = css`
  ${logoContainerBasicCss}
  ${breakpoints.breakpoint.s} {
    margin-top: 109px;
  }
`;

export const bannerLogoCss = css`
  ${logoCenterContainerBasicCss}
  margin-top: 68px;
`;

export const selectBasicStyle = {
  menu: (provided: CSSProperties): CSSProperties => ({
    ...provided,
    fontSize: 14,
  }),
  option: (provided: CSSProperties, { data, isSelected }: OptionStateStyle): CSSProperties => ({
    ...provided,
    fontSize: 14,
    borderRadius: 4,
    width: 'calc(100% - 16px)',
    margin: 8,
    color: isSelected ? ultraViolet : data.color,
  }),
  indicatorSeparator: (provided: CSSProperties): CSSProperties => ({
    ...provided,
    width: 0,
  }),
};

export const accordionDetailsPBLCss = (isMobile: boolean): SerializedStyles => css`
  padding: 18px ${isMobile ? '16px' : 0} 18px;
  max-width: 604px;
  margin: auto;
  border-top: 1px solid ${softGrey};
`;

export const containerCss = css`
  ${breakpoints.breakpoint.s} {
    width: 100%;
  }
`;
